import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {from, Observable, of} from 'rxjs';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { MasterDataService } from '../master-data/master-data-service';
import { SNACKBAR_LOGIN_ERROR, SNACKBAR_USER_ERROR } from '../../locale/consts.en';
import { switchMap, take } from 'rxjs/operators';
import { ADMIN_UUID } from '../users/users.model';
import { UsersService } from '../users/users.service';

import {
  Auth,
  User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut
} from 'firebase/auth';

import { firebaseAuth } from '../firebase'; // adjust path

export interface Credentials {
  email: string;
  password: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private auth: Auth = firebaseAuth;
  private masterDataService = inject(MasterDataService);
  private userService = inject(UsersService);
  private snackBar = inject(MatSnackBar);
  private router = inject(Router);

  token: string | null = null;
  user: User | null = null;

  get userName() {
    const user = this.user;
    return user ? (user.displayName ?? user.email) : null;
  }

  get userId() {
    return this.user ? this.user.uid : null;
  }

  readonly authState$ = new Observable<User | null>((subscriber) => {
    const unsubscribe = onAuthStateChanged(
      this.auth,
      (user) => {
        this.user = user;
        subscriber.next(user);
      },
      (error) => subscriber.error(error)
    );

    return unsubscribe;
  });

  getToken(): Observable<string | null> {
    return this.authState$.pipe(
      switchMap((user) => {
        if (!user) {
          return of(null);
        }
        return from(user.getIdToken());
      })
    );
  }

  async login({ email, password }: Credentials) {
    try {
      const credential = await signInWithEmailAndPassword(this.auth, email, password);
      this.user = credential.user;
      this.token = await this.user.getIdToken();
      await this.router.navigate(['/']);
    } catch (error) {
      const config = new MatSnackBarConfig();
      config.panelClass = ['app-snack-panel'];
      config.duration = 2500;
      config.verticalPosition = 'top';

      this.snackBar.open(SNACKBAR_LOGIN_ERROR, '', config);
      console.log('Login Error', error);
    }
  }

  async logout() {
    await signOut(this.auth);
    this.token = null;
    this.user = null;
    await this.router.navigate(['/login']);
  }

  autoAuth() {
    onAuthStateChanged(this.auth, (user) => {
      if (user) {
        this.user = user;

        user.getIdToken()
          .then((token: string) => {
            this.userService.getUserUuid(this.user!.uid)
              .pipe(
                take(1),
                switchMap((uuid) => this.userService.getUserData(uuid)),
              )
              .subscribe((resp) => {
                if (
                  resp === null ||
                  resp.enabled === false ||
                  !resp.roles.map((r) => r.uuid).includes(ADMIN_UUID)
                ) {
                  this.snackBar.open(SNACKBAR_USER_ERROR, '', { duration: 2000 });
                  this.logout();
                } else {
                  this.token = token;
                }
              });
          })
          .catch((error) => {
            console.log('User error', error);
          });
      } else {
        this.user = null;
        this.token = null;
        this.masterDataService.clearMasterData();
      }
    });
  }
}
